import { type ReactNode, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from "motion/react";
import { CheckCircle, Copy, MagnifyingGlass, MusicNote, OrangeSlice, Sparkle, WarningCircle, X } from "@phosphor-icons/react";
import stageDesktopAvif from "../assets/stage/stage-desktop.avif";
import stageDesktopJpg from "../assets/stage/stage-desktop.jpg";
import stageDesktopWebp from "../assets/stage/stage-desktop.webp";
import stageMobileAvif from "../assets/stage/stage-mobile.avif";
import stageMobileJpg from "../assets/stage/stage-mobile.jpg";
import stageMobileWebp from "../assets/stage/stage-mobile.webp";
import { api } from "../api";
import { ThemeButton } from "../components/ThemeButton";
import type { PublicBootstrap, PublicCategorySongs, PublicSong } from "../types";
import { copyText, normalizeSearch, renderCopyTemplate } from "../utils";

type Toast = { kind: "success" | "error"; text: string } | null;
type MotionMode = "full" | "lite" | "off";
type NetworkInformationLike = EventTarget & { saveData?: boolean };
const PUBLIC_REQUEST_TIMEOUT = 20_000;
const MINIMUM_LOADING_TIME = 600;
const SLOW_NETWORK_TIME = 6_000;

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function useAdaptiveMotion(): MotionMode {
  const reducedMotion = useReducedMotion();
  const coarsePointer = useMediaQuery("(pointer: coarse)");
  const narrowViewport = useMediaQuery("(max-width: 720px)");
  const [saveData, setSaveData] = useState(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    return Boolean(connection?.saveData);
  });

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    if (!connection) return;
    const update = () => setSaveData(Boolean(connection.saveData));
    connection.addEventListener("change", update);
    return () => connection.removeEventListener("change", update);
  }, []);

  if (reducedMotion) return "off";
  if (saveData || coarsePointer || narrowViewport) return "lite";
  return "full";
}

function StageBackdrop({ mode }: { mode: MotionMode }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  return (
    <div className="stage-backdrop" aria-hidden="true">
      <picture className={`stage-media${imageLoaded ? " loaded" : ""}`}>
        <source media="(max-width: 720px)" srcSet={stageMobileAvif} type="image/avif" />
        <source media="(max-width: 720px)" srcSet={stageMobileWebp} type="image/webp" />
        <source media="(max-width: 720px)" srcSet={stageMobileJpg} type="image/jpeg" />
        <source srcSet={stageDesktopAvif} type="image/avif" />
        <source srcSet={stageDesktopWebp} type="image/webp" />
        <img src={stageDesktopJpg} alt="" decoding="async" fetchPriority="low" onLoad={() => setImageLoaded(true)} />
      </picture>
      <motion.span
        className="stage-beam stage-beam-left"
        animate={mode === "full" ? { opacity: [0.32, 0.58, 0.32], rotate: [-13, -7, -13] } : undefined}
        transition={{ duration: 8, ease: "easeInOut", repeat: Infinity }}
      />
      <motion.span
        className="stage-beam stage-beam-right"
        animate={mode === "full" ? { opacity: [0.22, 0.5, 0.22], rotate: [14, 8, 14] } : undefined}
        transition={{ duration: 9.5, ease: "easeInOut", repeat: Infinity, delay: 0.8 }}
      />
      <motion.span
        className="stage-glow"
        animate={mode === "full" ? { opacity: [0.45, 0.72, 0.45], scale: [0.96, 1.05, 0.96] } : undefined}
        transition={{ duration: 6.5, ease: "easeInOut", repeat: Infinity }}
      />
    </div>
  );
}

function PublicFooter() {
  return (
    <footer className="public-footer">
      <span>BOBOCAST · 菠萝赛东点歌台</span>
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">津ICP备2024013210号</a>
    </footer>
  );
}

function PublicFrame({ children, mode }: { children: ReactNode; mode: MotionMode }) {
  return (
    <MotionConfig reducedMotion={mode === "off" ? "always" : "user"}>
      <div id="top" className="public-page" data-motion={mode}>
        <a className="skip-link" href="#main-content">跳到歌单内容</a>
        <StageBackdrop mode={mode} />
        <header className="public-topbar">
          <a className="brand-mark" href="#top" aria-label="返回页面顶部">
            <OrangeSlice size={22} weight="fill" />
            <span><strong>BOBOCAST</strong><small>LIVE SETLIST</small></span>
          </a>
          <ThemeButton />
        </header>
        {children}
        <PublicFooter />
      </div>
    </MotionConfig>
  );
}

function SongRow({ song, index, onCopy }: { song: PublicSong; index: number; onCopy: (song: PublicSong) => void }) {
  return (
    <article className="song-row">
      <span className="track-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
      <div className="song-main">
        <button className="song-title" type="button" onClick={() => onCopy(song)} title={`点击复制《${song.title}》`}>
          <span>{song.title}</span><Copy className="song-title-copy-icon" size={14} weight="bold" aria-hidden="true" />
        </button>
        {song.artist && <span className="song-artist">原唱 · {song.artist}</span>}
        {song.tags.length > 0 && <div className="tag-row">{song.tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}</div>}
      </div>
      <button className="copy-button" type="button" onClick={() => onCopy(song)} aria-label={`复制${song.title}的点歌文案`}>
        <Copy size={17} weight="bold" /><span>点这首</span>
      </button>
    </article>
  );
}

function SongListSkeleton() {
  return (
    <section className="song-section" aria-label="正在加载歌曲">
      <div className="skeleton heading-skeleton" />
      <div className="song-list skeleton-list">
        {[1, 2, 3, 4, 5].map((row) => <div className="skeleton row-skeleton" key={row} />)}
      </div>
    </section>
  );
}

function BrandLoading({ mode, slow }: { mode: MotionMode; slow: boolean }) {
  return (
    <main className="brand-loading" data-motion={mode} role="status" aria-live="polite" aria-label="正在加载歌单">
      <section className="brand-loading__panel">
        <div className="brand-loading__mark" aria-hidden="true">BO</div>
        <p className="brand-loading__eyebrow">BOBOCAST · LIVE SIGNAL</p>
        <h1>正在接入点歌台</h1>
        <p className="brand-loading__message">{slow ? "网络较慢，正在继续连接" : "准备歌单与舞台信息"}</p>
        <div className="brand-loading__signal" aria-hidden="true"><span /></div>
      </section>
    </main>
  );
}

export function PublicPage() {
  const motionMode = useAdaptiveMotion();
  const reducedMotion = motionMode === "off";
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const [hasPlayedListEntry, setHasPlayedListEntry] = useState(false);
  const [minimumLoadingElapsed, setMinimumLoadingElapsed] = useState(false);
  const [slowNetwork, setSlowNetwork] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  const discoveryRef = useRef<HTMLElement>(null);
  const listStartRef = useRef<HTMLDivElement>(null);

  const bootstrap = useQuery({
    queryKey: ["public-bootstrap"],
    queryFn: () => api<PublicBootstrap>("/api/public/bootstrap", { timeoutMs: PUBLIC_REQUEST_TIMEOUT }),
    staleTime: 30_000,
    retry: false,
  });
  const activeCategoryId = selectedCategoryId ?? bootstrap.data?.categories[0]?.id ?? null;
  const bundledCategory = bootstrap.data?.initial_category?.id === activeCategoryId ? bootstrap.data.initial_category : null;
  const normalizedQuery = normalizeSearch(query);
  const searching = Boolean(normalizedQuery);

  useEffect(() => {
    setMinimumLoadingElapsed(false);
    setSlowNetwork(false);
    const minimumTimer = window.setTimeout(() => setMinimumLoadingElapsed(true), MINIMUM_LOADING_TIME);
    const slowTimer = window.setTimeout(() => setSlowNetwork(true), SLOW_NETWORK_TIME);
    return () => {
      window.clearTimeout(minimumTimer);
      window.clearTimeout(slowTimer);
    };
  }, [loadAttempt]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(normalizedQuery), 250);
    return () => window.clearTimeout(timeout);
  }, [normalizedQuery]);

  useEffect(() => {
    if (selectedCategoryId !== null && bootstrap.data && !bootstrap.data.categories.some((category) => category.id === selectedCategoryId)) {
      setSelectedCategoryId(null);
    }
  }, [bootstrap.data, selectedCategoryId]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    const discovery = discoveryRef.current;
    const shell = discovery?.parentElement;
    if (!discovery || !shell) return;
    const updateHeight = () => shell.style.setProperty("--discovery-height", `${discovery.offsetHeight}px`);
    const observer = new ResizeObserver(updateHeight);
    observer.observe(discovery);
    updateHeight();
    return () => {
      observer.disconnect();
      shell.style.removeProperty("--discovery-height");
    };
  }, [bootstrap.data, searching]);

  const categorySongs = useQuery({
    queryKey: ["public-category-songs", activeCategoryId],
    queryFn: () => api<PublicCategorySongs>(`/api/public/categories/${activeCategoryId}/songs`, { timeoutMs: PUBLIC_REQUEST_TIMEOUT }),
    enabled: activeCategoryId !== null && !normalizedQuery && !bundledCategory,
    staleTime: 30_000,
    retry: false,
  });
  const searchResults = useQuery({
    queryKey: ["public-song-search", debouncedQuery],
    queryFn: () => api<PublicSong[]>(`/api/public/songs/search?q=${encodeURIComponent(debouncedQuery)}`, { timeoutMs: PUBLIC_REQUEST_TIMEOUT }),
    enabled: Boolean(debouncedQuery),
    staleTime: 30_000,
    retry: false,
  });

  if (bootstrap.isLoading || !minimumLoadingElapsed) return <BrandLoading mode={motionMode} slow={slowNetwork} />;
  if (bootstrap.isError || !bootstrap.data) {
    return (
      <PublicFrame mode={motionMode}>
        <main id="main-content" className="public-state">
          <WarningCircle size={42} weight="duotone" />
          <p className="state-kicker">SIGNAL LOST</p>
          <h1>歌单暂时没有加载成功</h1>
          <p>连接超过 20 秒或网络已中断，请检查网络后重试。</p>
          <button className="primary-button" type="button" onClick={() => { setLoadAttempt((value) => value + 1); void bootstrap.refetch(); }}>重新加载</button>
        </main>
      </PublicFrame>
    );
  }

  const { settings, categories, total_songs: totalSongs } = bootstrap.data;
  const activeCategory = categories.find((category) => category.id === activeCategoryId) ?? categories[0];
  const searchPending = searching && (normalizedQuery !== debouncedQuery || searchResults.isLoading);
  const results = searchResults.data ?? [];
  const listEntryInitial = reducedMotion || hasPlayedListEntry
    ? false
    : motionMode === "full"
      ? { opacity: 0, y: 32, scale: 0.985 }
      : { opacity: 0, y: 14 };
  const listEntryTransition = {
    duration: motionMode === "full" ? 0.68 : 0.36,
    delay: motionMode === "full" ? 0.12 : 0,
    ease: [0.16, 1, 0.3, 1] as const,
  };
  const finishListEntry = () => {
    if (!hasPlayedListEntry) setHasPlayedListEntry(true);
  };
  const showToast = (next: Toast) => {
    setToast(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 2200);
  };
  const handleCopy = async (song: PublicSong) => {
    try {
      await copyText(renderCopyTemplate(settings.copy_template, song, settings));
      showToast({ kind: "success", text: `已复制《${song.title}》` });
    } catch {
      showToast({ kind: "error", text: "复制失败，请长按歌名手动复制" });
    }
  };
  const scrollToListStart = () => {
    const target = listStartRef.current;
    const discovery = discoveryRef.current;
    if (!target || !discovery) return;
    const sticky = window.getComputedStyle(discovery).position === "sticky";
    const stickyTop = sticky ? Number.parseFloat(window.getComputedStyle(discovery).top) || 0 : 0;
    const offset = sticky ? discovery.offsetHeight + stickyTop + 18 : 18;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? "auto" : "smooth" });
  };
  const selectCategory = (id: number) => {
    setSelectedCategoryId(id);
    window.requestAnimationFrame(scrollToListStart);
  };

  return (
    <PublicFrame mode={motionMode}>
      <main id="main-content" className="public-shell">
        <motion.section
          className="profile"
          aria-labelledby="site-title"
          initial={reducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionMode === "full" ? 0.65 : 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div
            className="avatar-stage"
            initial={reducedMotion ? false : { opacity: 0, scale: 0.88, rotate: -5 }}
            animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ type: "spring", stiffness: 150, damping: 18, delay: motionMode === "full" ? 0.12 : 0 }}
          >
            <span className="avatar-orbit" aria-hidden="true" />
            <div className="avatar-wrap">
              {settings.avatar_url ? <img src={settings.avatar_url} alt={`${settings.singer_name}的头像`} className="avatar" /> : <div className="avatar-fallback" aria-hidden="true">菠</div>}
            </div>
          </motion.div>
          <motion.div
            className="profile-copy"
            initial={reducedMotion ? false : { opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: motionMode === "full" ? 0.2 : 0 }}
          >
            <p className="stage-kicker"><Sparkle size={15} weight="fill" />NOW ON STAGE · {settings.singer_name}</p>
            <h1 id="site-title">{settings.site_title}</h1>
            {settings.bio && <p className="bio">{settings.bio}</p>}
            {settings.announcement && <div className="announcement"><MusicNote size={17} weight="fill" /><span>{settings.announcement}</span></div>}
          </motion.div>
          <motion.div
            className="song-count"
            initial={reducedMotion ? false : { opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: motionMode === "full" ? 0.28 : 0 }}
          >
            <span className="count-label">AVAILABLE TRACKS</span>
            <strong>{totalSongs}</strong>
            <span>首可点歌曲</span>
          </motion.div>
          <div className="stage-ticker" aria-hidden="true"><span>SONG REQUEST</span><i /><span>SEARCH</span><i /><span>COPY &amp; SEND</span></div>
        </motion.section>

        <section ref={discoveryRef} className="discovery" aria-label="搜索与分类导航">
          <label className="search-box">
            <MagnifyingGlass size={21} weight="bold" />
            <span className="sr-only">搜索歌曲</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索歌名、原唱、别名或关键词" autoComplete="off" />
            <span className="search-hint" aria-hidden="true">SEARCH</span>
            {query && <button type="button" onClick={() => setQuery("")} aria-label="清空搜索"><X size={18} weight="bold" /></button>}
          </label>
          {!searching && categories.length > 0 && (
            <nav className="category-nav" aria-label="歌曲分类">
              {categories.map((category) => {
                const active = category.id === activeCategory?.id;
                return (
                  <button
                    type="button"
                    key={category.id}
                    className={active ? "active" : undefined}
                    aria-pressed={active}
                    onClick={() => selectCategory(category.id)}
                  >
                    {active && <motion.span className="category-active" layoutId="active-category" transition={{ type: "spring", stiffness: 360, damping: 32 }} />}
                    <span className="category-label">{category.name}<small>{category.song_count}</small></span>
                  </button>
                );
              })}
            </nav>
          )}
        </section>

        <div ref={listStartRef} className="list-start" aria-hidden="true" />

        <AnimatePresence mode="wait" initial={false}>
          {searching ? (
            <motion.div key="search" initial={reducedMotion ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {searchPending ? <SongListSkeleton /> : searchResults.isError ? (
                <div className="empty-state"><WarningCircle size={34} /><p className="state-kicker">SEARCH ERROR</p><h3>搜索没有加载成功</h3><p>请检查网络后再试一次。</p><button className="secondary-button" type="button" onClick={() => searchResults.refetch()}>重新搜索</button></div>
              ) : (
                <motion.section
                  className="results-section"
                  data-entry-motion={reducedMotion ? "off" : hasPlayedListEntry ? "complete" : motionMode}
                  initial={listEntryInitial}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={listEntryTransition}
                  onAnimationComplete={finishListEntry}
                >
                  <div className="section-heading"><div><span>SEARCH RESULTS</span><h2>搜索结果</h2></div><strong>{results.length} TRACKS</strong></div>
                  {results.length ? <div className="song-list">{results.map((song, index) => <SongRow key={song.id} song={song} index={index} onCopy={handleCopy} />)}</div> : (
                    <div className="empty-state"><MagnifyingGlass size={34} /><p className="state-kicker">NO MATCH</p><h3>没有找到相关歌曲</h3><p>可以尝试更短的歌名、原唱或别名。</p><button className="secondary-button" type="button" onClick={() => setQuery("")}>查看歌曲分类</button></div>
                  )}
                </motion.section>
              )}
            </motion.div>
          ) : categories.length && activeCategory ? (
            <motion.div key={activeCategory.id} initial={reducedMotion ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
              {!bundledCategory && categorySongs.isLoading ? <SongListSkeleton /> : !bundledCategory && categorySongs.isError ? (
                <div className="empty-state"><WarningCircle size={34} /><p className="state-kicker">SETLIST ERROR</p><h3>这个分类没有加载成功</h3><p>请检查网络后再试一次。</p><button className="secondary-button" type="button" onClick={() => categorySongs.refetch()}>重新加载</button></div>
              ) : (
                <motion.section
                  className="song-section"
                  data-entry-motion={reducedMotion ? "off" : hasPlayedListEntry ? "complete" : motionMode}
                  initial={listEntryInitial}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={listEntryTransition}
                  onAnimationComplete={finishListEntry}
                >
                  <div className="section-heading"><div><span>LIVE SETLIST</span><h2>{activeCategory.name}</h2></div><strong>{activeCategory.song_count} TRACKS</strong></div>
                  {(bundledCategory ?? categorySongs.data)?.songs.length ? <div className="song-list">{(bundledCategory ?? categorySongs.data)!.songs.map((song, index) => <SongRow key={song.id} song={song} index={index} onCopy={handleCopy} />)}</div> : <p className="category-empty">这个分类还没有已发布的歌曲。</p>}
                </motion.section>
              )}
            </motion.div>
          ) : (
            <div className="empty-state"><MusicNote size={38} /><p className="state-kicker">SOUND CHECK</p><h2>歌单正在准备中</h2><p>稍后再来看看吧。</p></div>
          )}
        </AnimatePresence>
      </main>
      <AnimatePresence>
        {toast && (
          <motion.div className={`toast ${toast.kind}`} role="status" aria-live="polite" initial={reducedMotion ? false : { opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8 }}>
            {toast.kind === "success" ? <CheckCircle size={20} weight="fill" /> : <WarningCircle size={20} weight="fill" />}{toast.text}
          </motion.div>
        )}
      </AnimatePresence>
    </PublicFrame>
  );
}
